import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CreateVideo from "./CreateVideo";
import TemplateToVideo from "./TemplateToVideo";

const Video: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"t2v" | "i2v" | "template2v">("t2v");

  // Update active tab based on URL
  useEffect(() => {
    const path = location.pathname;

    if (path === "/video" || path === "/video/t2v") {
      setActiveTab("t2v");
      if (path === "/video") {
        navigate("/video/t2v", { replace: true });
      }
    } else if (path === "/video/i2v") {
      setActiveTab("i2v");
    } else if (path === "/video/template2v") {
      setActiveTab("template2v");
    }
  }, [location.pathname, navigate]);

  const handleTabClick = (tab: "t2v" | "i2v" | "template2v") => {
    setActiveTab(tab);
    navigate(`/video/${tab}`);
  };

  const tabs = [
    {
      id: "t2v",
      label: "Text to Video",
      shortLabel: "Text",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      ),
    },
    {
      id: "i2v",
      label: "Image to Video",
      shortLabel: "Image",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      id: "template2v",
      label: "Template to Video",
      shortLabel: "Template",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className="flex flex-col h-full bg-black dark:bg-black">
      {/* Tab Navigation */}
      <div className="border-b border-white/10 dark:border-white/10 px-4 sm:px-6 pt-6 pb-0 transition-colors duration-300">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-1.5 md:gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id as any)}
                className={`
                  relative flex items-center gap-2 px-3 md:px-6 py-2.5 md:py-3 rounded-t-lg text-sm md:text-base font-medium transition-all duration-200 
                  ${
                    activeTab === tab.id
                      ? "bg-black/60 dark:bg-black/60 text-white dark:text-white"
                      : "text-gray-400 dark:text-gray-400 hover:text-gray-300 dark:hover:text-gray-300 hover:bg-white/5 dark:hover:bg-white/5"
                  }
                `}
              >
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white dark:bg-white" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-hide overscroll-none">
        {activeTab === "t2v" && <CreateVideo mode="t2v" />}
        {activeTab === "i2v" && <CreateVideo mode="i2v" />}
        {activeTab === "template2v" && <TemplateToVideo />}
      </div>
    </div>
  );
};

export default Video;
